import {
  Box,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow as MuiTableRow,
  TableSortLabel,
} from '@mui/material';
import { useTranslate } from '@si/utils';
import type { JSX } from 'react';
import { createRef, useMemo, useState } from 'react';

import { Text } from '../Text/Text';
import { useColors } from '../ThemeProvider';
import { TableBodySkeleton } from './TableBodySkeleton';
import { TableRow } from './TableRow';

const containerRef = createRef<HTMLDivElement>();

interface Props<T extends string> {
  variant?: 'default' | 'people';
  columns: {
    label: string | JSX.Element;
    widthPercentage?: string;
    align?: 'left' | 'right';
    sortKey?: T;
  }[];
  rows: JSX.Element[][];
  links?: string[];
  expandableContent?: JSX.Element[];
  isLoading?: boolean;
  emptyText?: string;
  totalRows?: number;
  activeSortKey?: T;
  setActiveSortKey?: (sortKey: T) => void;
}

export function Table<T extends string>({
  variant = 'default',
  columns,
  rows,
  links,
  expandableContent,
  isLoading = false,
  emptyText = '',
  totalRows,
  activeSortKey,
  setActiveSortKey = () => {},
}: Props<T>) {
  const { t, translate } = useTranslate();
  const { primary } = useColors();
  // TODO ICS-360 - Default sorting fix
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const directedRows = useMemo(
    () => (sortDirection === 'desc' ? [...rows].reverse() : rows),
    [rows, sortDirection]
  );

  return (
    <div ref={containerRef}>
      {!isLoading && !!totalRows && (
        <Text>
          {translate(t.showCountOf, {
            showCount: rows.length,
            total: totalRows,
          })}
        </Text>
      )}

      <TableContainer
        sx={
          variant === 'people'
            ? {
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '0.5rem',
                bgcolor: 'background.paper',
                '& > table': { minWidth: columns.length > 7 ? '65rem' : '45rem' },
                '& > table > thead': { bgcolor: 'action.hover', borderBottom: 0 },
                '& > table > thead th': {
                  px: '1rem',
                  py: '0.75rem',
                  borderBottom: 0,
                  whiteSpace: 'nowrap',
                  '& p': {
                    fontSize: '0.75rem',
                    color: 'text.primary',
                    '& strong': { fontWeight: 500 },
                  },
                },
                '& > table > tbody > tr > td': {
                  px: '1rem',
                  py: '0.75rem',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  verticalAlign: 'middle',
                  '& > p, & > div > p': { fontSize: '0.75rem', color: 'text.secondary' },
                },
                '& > table > tbody > tr:last-child > td': { borderBottom: 0 },
                '& > table > tbody > tr > td:first-of-type': expandableContent
                  ? { width: '1rem', pr: 0, pl: '0.75rem' }
                  : undefined,
                ['& > table > tbody > tr > td:nth-of-type(' + (expandableContent ? 2 : 1) + ')']: {
                  '& > p, & > div > p': {
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    color: 'text.primary',
                  },
                },
                '& .MuiIconButton-root': {
                  width: '1.75rem',
                  height: '1.75rem',
                  '& svg': { fontSize: '1rem' },
                },
              }
            : undefined
        }
      >
        <MuiTable aria-label="table">
          {variant === 'people' && (
            <colgroup>
              {!!expandableContent && <col style={{ width: '2rem' }} />}
              {columns.map(({ widthPercentage }, index) => (
                <col
                  key={index}
                  style={{
                    width:
                      expandableContent && index === 0 && widthPercentage
                        ? `calc(${widthPercentage} - 2rem)`
                        : widthPercentage,
                  }}
                />
              ))}
            </colgroup>
          )}
          <TableHead sx={{ borderBottom: '2px solid', borderBottomColor: primary }}>
            <MuiTableRow>
              {!!expandableContent && variant !== 'people' && <TableCell width="1rem" />}

              {columns.map(({ label, widthPercentage, sortKey, align }, index) => (
                <TableCell
                  key={index}
                  colSpan={variant === 'people' && expandableContent && index === 0 ? 2 : undefined}
                  width={variant === 'people' ? undefined : (widthPercentage ?? 'auto')}
                  sortDirection={activeSortKey === label ? sortDirection : false}
                >
                  {sortKey ? (
                    <TableSortLabel
                      active={activeSortKey === sortKey}
                      direction={sortDirection}
                      onClick={() => {
                        setActiveSortKey(sortKey);
                        setSortDirection((prev) =>
                          prev === 'asc' && activeSortKey === sortKey ? 'desc' : 'asc'
                        );
                      }}
                    >
                      {typeof label === 'string' ? (
                        <Text bold align={align ?? 'left'}>
                          {label}
                        </Text>
                      ) : (
                        label
                      )}
                    </TableSortLabel>
                  ) : typeof label === 'string' ? (
                    <Text bold align={align ?? 'left'}>
                      {label}
                    </Text>
                  ) : (
                    label
                  )}
                </TableCell>
              ))}
            </MuiTableRow>
          </TableHead>

          <TableBody>
            {isLoading && <TableBodySkeleton nrOfColumns={columns.length + 1} />}

            {!isLoading &&
              directedRows.map((row, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  variant={variant}
                  row={row}
                  expandableContent={expandableContent?.[rowIndex]}
                  link={links?.[rowIndex]}
                />
              ))}
          </TableBody>

          {rows.length === 0 && !isLoading && (
            <caption>
              <Box
                sx={{
                  display: 'grid',
                  placeItems: 'center',
                  padding: '1.5rem 0',
                }}
              >
                <Text>{emptyText || translate(t.noResults)}</Text>
              </Box>
            </caption>
          )}
        </MuiTable>
      </TableContainer>

      {!isLoading && !!totalRows && (
        <Text>
          {translate(t.showCountOf, {
            showCount: rows.length,
            total: totalRows,
          })}
        </Text>
      )}
    </div>
  );
}
